const runWithLoadingLock = ({ isMountedRef, isLoadingDataRef, action, timeoutMs = 500 }) => {
  if (!isMountedRef.current) {
    return;
  }

  isLoadingDataRef.current = true;
  action();
  setTimeout(() => {
    isLoadingDataRef.current = false;
  }, timeoutMs);
};

const loadSiteForEditMode = ({
  formik,
  adminBuildingsList,
  loadedDataRef,
  onSiteChange,
  isMountedRef,
  isLoadingDataRef,
}) => {
  const site = formik.values?.site;
  const shouldLoadSite = site && adminBuildingsList.length === 0 && onSiteChange && loadedDataRef.current.site !== site;
  if (!shouldLoadSite) {
    return;
  }

  loadedDataRef.current.site = site;
  runWithLoadingLock({
    isMountedRef,
    isLoadingDataRef,
    action: () => onSiteChange(site),
  });
};

const loadBuildingForEditMode = ({
  formik,
  adminBuildingsList,
  adminFloorsList,
  loadedDataRef,
  sortedBuildings,
  onBuildingChange,
  isMountedRef,
  isLoadingDataRef,
}) => {
  const building = formik.values?.building;
  const canAttemptBuildingLoad = building && adminFloorsList.length === 0 && adminBuildingsList.length > 0;
  if (!canAttemptBuildingLoad || loadedDataRef.current.building === building) {
    return;
  }

  const matchingBuilding = sortedBuildings.find((item) => item.name === building);
  if (!matchingBuilding || !onBuildingChange) {
    return;
  }

  loadedDataRef.current.building = building;
  runWithLoadingLock({
    isMountedRef,
    isLoadingDataRef,
    action: () => onBuildingChange(matchingBuilding.id),
  });
};

const loadFloorForEditMode = ({
  formik,
  adminFloorsList,
  adminRoomsList,
  loadedDataRef,
  sortedFloors,
  onFloorChange,
  isMountedRef,
  isLoadingDataRef,
}) => {
  const floor = formik.values?.floor;
  const canAttemptFloorLoad = floor && adminRoomsList.length === 0 && adminFloorsList.length > 0;
  if (!canAttemptFloorLoad || loadedDataRef.current.floor === floor) {
    return;
  }

  const matchingFloor = sortedFloors.find((item) => item.name === floor);
  if (!matchingFloor || !onFloorChange) {
    return;
  }

  loadedDataRef.current.floor = floor;
  runWithLoadingLock({
    isMountedRef,
    isLoadingDataRef,
    action: () => onFloorChange(matchingFloor.id),
  });
};

export const loadEditModeLocationData = (ctx) => {
  const {
    isEditMode,
    dialogOpen,
    isLoadingDataRef,
    formik,
    adminBuildingsList,
    adminFloorsList,
    adminRoomsList,
    loadedDataRef,
    sortedBuildings,
    sortedFloors,
    onSiteChange,
    onBuildingChange,
    onFloorChange,
    isMountedRef,
  } = ctx;

  if (!isEditMode || !dialogOpen || isLoadingDataRef.current) {
    return;
  }

  loadSiteForEditMode({
    formik,
    adminBuildingsList,
    loadedDataRef,
    onSiteChange,
    isMountedRef,
    isLoadingDataRef,
  });

  loadBuildingForEditMode({
    formik,
    adminBuildingsList,
    adminFloorsList,
    loadedDataRef,
    sortedBuildings,
    onBuildingChange,
    isMountedRef,
    isLoadingDataRef,
  });

  loadFloorForEditMode({
    formik,
    adminFloorsList,
    adminRoomsList,
    loadedDataRef,
    sortedFloors,
    onFloorChange,
    isMountedRef,
    isLoadingDataRef,
  });
};

export const syncEditModeDropdownValues = ({
  isEditMode,
  dialogOpen,
  floorDropdownRef,
  roomDropdownRef,
  formik,
  sortedFloors,
  sortedRooms,
}) => {
  if (!isEditMode || !dialogOpen) {
    return undefined;
  }

  const timer = setTimeout(() => {
    if (floorDropdownRef.current && formik.values?.floor && sortedFloors.length > 0) {
      const floorIndex = sortedFloors.findIndex((floor) => floor.name === formik.values.floor);
      if (floorIndex >= 0) {
        floorDropdownRef.current.value = formik.values.floor;
      }
    }

    if (roomDropdownRef.current && sortedRooms.length > 0 && formik.values?.room) {
      const roomIndex = sortedRooms.findIndex((room) => room.name === formik.values.room);
      if (roomIndex >= 0) {
        roomDropdownRef.current.value = formik.values.room;
      }
    }
  }, 500);

  return () => clearTimeout(timer);
};

export const trackInitialRoomValue = ({ previousRoomValue, formik, initialRoomValue, isEditMode }) => {
  if (previousRoomValue.current === formik.values?.room) {
    return;
  }

  previousRoomValue.current = formik.values?.room;

  if (!initialRoomValue.current && formik.values?.room && isEditMode) {
    initialRoomValue.current = formik.values.room;
  }
};

export const restoreInitialRoomIfAvailable = ({ isEditMode, sortedRooms, formik, initialRoomValue, isMountedRef }) => {
  if (!isEditMode || sortedRooms.length === 0 || formik.values?.room || !initialRoomValue.current) {
    return;
  }

  const roomExists = sortedRooms.some((room) => room.name === initialRoomValue.current);
  if (roomExists && isMountedRef.current) {
    formik.setFieldValue("room", initialRoomValue.current);
  }
};

export const initializeDialogState = ({
  isEditMode,
  dialogOpen,
  hasUserInteractedWithSite,
  initialRoomValue,
  initialValuesLoaded,
  loadedDataRef,
  formik,
  isInitialLoad,
  setInputValue,
  setModelSearchTerm,
}) => {
  if (isEditMode && dialogOpen) {
    isInitialLoad.current = true;
    hasUserInteractedWithSite.current = false;
    initialRoomValue.current = null;
    initialValuesLoaded.current = false;
    loadedDataRef.current = {
      site: null,
      building: null,
      floor: null,
    };
    if (formik.values?.site && formik.values?.building && formik.values?.floor && formik.values?.room) {
      initialValuesLoaded.current = true;
    }
    setTimeout(() => {
      isInitialLoad.current = false;
    }, 1500);
  }

  if (!dialogOpen) {
    setInputValue("");
    setModelSearchTerm("");
    return;
  }

  if (isEditMode && formik.values?.model) {
    setInputValue(formik.values.model);
  }
};

export const resetInteractionOnEditTargetSwitch = ({
  isEditMode,
  dialogOpen,
  formik,
  lastEditGroupId,
  hasUserInteractedWithSite,
  isInitialLoad,
}) => {
  if (!isEditMode || !dialogOpen) {
    return;
  }

  const currentEditGroupId = formik.values.model || null;
  if (lastEditGroupId.current && lastEditGroupId.current !== currentEditGroupId) {
    hasUserInteractedWithSite.current = false;
    isInitialLoad.current = true;
    setTimeout(() => {
      isInitialLoad.current = false;
    }, 1500);
  }
  lastEditGroupId.current = currentEditGroupId;
};

export const createSiteSelectChangeHandler = (ctx) => {
  const {
    sortedSites,
    formik,
    isInitialLoad,
    initialValuesLoaded,
    isEditMode,
    hasUserInteractedWithSite,
    isMountedRef,
    isLoadingDataRef,
    onSiteChange,
  } = ctx;

  return (e) => {
    if (e.detail === null) {
      return;
    }

    const selectedSite = sortedSites[e.detail];
    const newSiteValue = selectedSite?.siteName || "";
    if (newSiteValue === formik.values.site) {
      return;
    }

    formik.setFieldValue("site", newSiteValue);

    if (!isInitialLoad.current || (initialValuesLoaded.current && isEditMode)) {
      formik.setFieldValue("building", "");
      formik.setFieldValue("floor", "");
      formik.setFieldValue("room", "");
      hasUserInteractedWithSite.current = true;
    }

    if (isMountedRef.current && !isLoadingDataRef.current) {
      onSiteChange?.(selectedSite.siteName);
    }
  };
};

export const createBuildingSelectChangeHandler = (ctx) => {
  const {
    sortedBuildings,
    formik,
    isInitialLoad,
    initialValuesLoaded,
    isEditMode,
    isMountedRef,
    isLoadingDataRef,
    onBuildingChange,
  } = ctx;

  return (e) => {
    if (e.detail === null) {
      return;
    }

    const selectedBuilding = sortedBuildings[e.detail];
    const newBuildingValue = selectedBuilding?.name || "";
    if (newBuildingValue === formik.values.building) {
      return;
    }

    formik.setFieldValue("building", newBuildingValue);

    if (!isInitialLoad.current || (initialValuesLoaded.current && isEditMode)) {
      formik.setFieldValue("floor", "");
      formik.setFieldValue("room", "");
    }

    if (isMountedRef.current && !isLoadingDataRef.current) {
      onBuildingChange?.(selectedBuilding.id);
    }
  };
};

export const createFloorSelectChangeHandler = (ctx) => {
  const {
    sortedFloors,
    formik,
    isInitialLoad,
    initialValuesLoaded,
    isEditMode,
    isMountedRef,
    isLoadingDataRef,
    onFloorChange,
  } = ctx;

  return (e) => {
    if (e.detail === null) {
      return;
    }

    const selectedFloor = sortedFloors[e.detail];
    const newFloorValue = selectedFloor?.name || "";
    if (newFloorValue === formik.values.floor) {
      return;
    }

    formik.setFieldValue("floor", newFloorValue);

    if (!isInitialLoad.current || (initialValuesLoaded.current && isEditMode)) {
      formik.setFieldValue("room", "");
    }

    if (isMountedRef.current && !isLoadingDataRef.current) {
      onFloorChange?.(selectedFloor.id);
    }
  };
};

export const createRoomSelectChangeHandler = ({ sortedRooms, formik }) => {
  return (e) => {
    if (e.detail === null) {
      return;
    }

    const selectedRoom = sortedRooms[e.detail];
    formik.setFieldValue("room", selectedRoom?.name || "");
  };
};

export const getFloorDropdownKey = (floorsLength, floorValue) => `floor-${floorsLength}-${floorValue || "empty"}`;

export const getRoomDropdownKey = (roomsLength, roomValue) => `room-${roomsLength}-${roomValue || "empty"}`;

export const isSiteDropdownDisabled = ({ isEditMode, groupValue }) => !isEditMode && !groupValue;

export const isBuildingDropdownDisabled = ({ isEditMode, buildingsLength, siteValue }) =>
  !isEditMode && (buildingsLength === 0 || !siteValue);

export const isFloorDropdownDisabled = ({ isEditMode, floorsLength, buildingValue }) =>
  !isEditMode && (floorsLength === 0 || !buildingValue);

export const isRoomDropdownDisabled = ({ isEditMode, roomsLength, floorValue }) =>
  !isEditMode && (roomsLength === 0 || !floorValue);

export const getNamedItemLabelWithId = (item, idField) => {
  if (!item) {
    return "";
  }

  const suffix = item[idField] ? ` (ID: ${item[idField]})` : "";
  return `${item.name}${suffix}`;
};
